"use client";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CopyPlusIcon, MoreVerticalIcon, TrashIcon } from "lucide-react";
import { EditIngredientDialog } from "./edit-ingredient-dialog";
import type { Ingredient } from "@/lib/types";
import { useRouter } from "@tanstack/react-router";
import { trackEvent } from "@/lib/analytics";
import { useSession } from "@/lib/auth-client";
import { useIsClient } from "@/lib/hooks/useIsClient";
import { DuplicateIngredientDialog } from "./duplicate-ingredient-dialog";
import { SignInDialogWithTrigger } from "../auth/sign-in-dialog";
import { Skeleton } from "../ui/skeleton";
import { DeleteIngredientDialog } from "./delete-ingredient-dialog";
type IngredientDetailActionsProps = {
  ingredient: Ingredient;
  canEdit?: boolean;
};
export function IngredientDetailActionsContent({
  ingredient,
  canEdit = false,
}: IngredientDetailActionsProps) {
  const router = useRouter();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const handleUpdate = () => {
    setShowEditDialog(false);
    router.invalidate();
  };
  if (!canEdit) {
    return (
      <DuplicateIngredientDialog ingredient={ingredient}>
        <Button>Duplicate</Button>
      </DuplicateIngredientDialog>
    );
  }
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger render={<Button variant="outline" size="icon" />}>
          <MoreVerticalIcon className="h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DuplicateIngredientDialog
            ingredient={ingredient}
            triggerNativeButton={false}
          >
            <DropdownMenuItem closeOnClick={false}>
              <CopyPlusIcon /> Duplicate
            </DropdownMenuItem>
          </DuplicateIngredientDialog>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setShowDeleteDialog(true)}
            variant="destructive"
          >
            <TrashIcon /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DeleteIngredientDialog
        ingredient={ingredient}
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onDeleted={() => {
          trackEvent("ingredient-detail-deleted");
          router.navigate({ to: "/ingredients" });
        }}
      />
      <EditIngredientDialog
        ingredient={ingredient}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        onSuccess={handleUpdate}
      />
      <Button onClick={() => setShowEditDialog(true)}>Edit</Button>
    </>
  );
}
export function IngredientDetailActions({
  ingredient,
}: IngredientDetailActionsProps) {
  const isClient = useIsClient();
  const { data: session, isPending } = useSession();
  const canEdit = session?.user?.id === ingredient.createdBy;
  if (!isClient || isPending) {
    return <Skeleton className="h-9 w-32" />;
  }
  if (!session) {
    return (
      <SignInDialogWithTrigger analyticsFrom="ingredient-details">
        <Button>Duplicate</Button>
      </SignInDialogWithTrigger>
    );
  }
  return (
    <IngredientDetailActionsContent ingredient={ingredient} canEdit={canEdit} />
  );
}
